/**
 * The one sentence Settings shows about an existing subscription (BEN-141 item 9).
 *
 * Lives here rather than inside `pages/Settings.tsx` so it can be tested without
 * mounting the page: the branching is the part that can be wrong, and one branch
 * of it — `cancel_at_period_end` — is the kind of bug that only shows up for a
 * user who has already cancelled, i.e. never in local clicking.
 *
 * WHY EVERY BRANCH NAMES CANCELLATION OR THE PORTAL. Terms §5.6, published
 * 2026-08-19, states that cancellation is available "through account settings or
 * the Stripe Customer Portal". A button alone satisfies that in mechanism but not
 * in words; a user scanning the page for how to cancel should read the word.
 */
import {
  billingDateLabel,
  billingDaysUntil,
  trialEndedCopy,
  trialRenewalCopy,
} from "@fomofi/shared";
import type { SubscriptionState } from "@/api/payments";
import { trialLength } from "@/lib/trial";

/** `now` and `timeZone` pin the clock and zone for tests; the app omits both. */
export interface BillingCopyOptions {
  now?: Date;
  timeZone?: string;
}

export function describeBilling(
  state: SubscriptionState,
  { now = new Date(), timeZone }: BillingCopyOptions = {},
): string {
  const until = formatBillingDate(state.current_period_end, timeZone);
  const trialEnds =
    state.status === "trialing" ? formatBillingDate(state.trial_end, timeZone) : null;

  // A trial cancelled in the portal: Stripe sets `cancel_at` to the trial end. Name
  // the trial, so its date does not vanish behind "Set to cancel" (Anthony, 10/9),
  // and say what the user is checking for: no charge.
  if (state.cancel_at_period_end && trialEnds) {
    return `Free trial ends ${trialEnds}, and the subscription is set to cancel then, so you won't be charged. Reopen the portal to resume.`;
  }

  // CHECKED BEFORE `status`, and this ordering is the point. A subscription that
  // is already scheduled to cancel still reports `active` (or `trialing`) right
  // up to the period end, so switching on status first would tell someone who
  // has cancelled that they are renewing — the exact opposite of the truth, on
  // the screen they would go to in order to check.
  if (state.cancel_at_period_end) {
    return until
      ? `Set to cancel on ${until}. You keep access until then. Reopen the portal to resume.`
      : "Set to cancel at the end of the current period. Reopen the portal to resume.";
  }

  switch (state.status) {
    case "trialing":
      // Anthony, 10/9: the trial end AND the renewal after it, so the one-period gap
      // between them can be checked on this screen.
      return trialEnds && state.trial_end
        ? `Free trial, ending ${trialEnds}. ${trialRenewalCopy(state.trial_end, state.plan, timeZone)} Update your card, switch plans, or cancel in the Stripe portal.`
        : "Free trial. Update your card, switch plans, or cancel in the Stripe portal.";
    case "active": {
      // In the first paid period, the trial date the renewal follows from.
      const ended = trialEndedCopy(
        state.trial_end,
        state.current_period_end,
        state.plan,
        now,
        timeZone,
      );
      return until
        ? `${ended ? `${ended} ` : ""}Active, renewing ${until}. Update your card, switch plans, or cancel in the Stripe portal.`
        : "Active. Update your card, switch plans, or cancel in the Stripe portal.";
    }
    case "past_due":
    case "unpaid":
      return "Payment failed. Update your card in the Stripe portal to keep your access.";
    case "incomplete":
      return "Payment needs confirming. Finish it in the Stripe portal.";
    case "paused":
      return "Paused. Resume or cancel in the Stripe portal.";
    case "canceled":
    case "incomplete_expired":
      return "Cancelled. Your billing history and receipts are in the Stripe portal.";
    // `none` never reaches here — the caller renders the no-subscription branch
    // instead, because there is no Stripe customer to open a portal session for.
    default:
      return "Update your card, switch plans, or cancel in the Stripe portal.";
  }
}

const PLAN_NAMES: Record<Exclude<SubscriptionState["plan"], "">, string> = {
  monthly: "Monthly",
  yearly: "Yearly",
};

const STRIPE_LINE =
  "Billing is handled securely by Stripe, and we never see or store your card details.";

/**
 * The subtitle under the /payment heading. Once there is a subscription it
 * describes that one instead of inviting the user to start a trial they
 * already started (m-payment.png, 2026-09-25 walkthrough). The cancel check
 * comes before `status` for the same reason as in `describeBilling`.
 */
export function paymentPageSubtitle(
  state: SubscriptionState | undefined,
  { inSignupFlow, now = new Date(), timeZone }: { inSignupFlow: boolean } & BillingCopyOptions,
): string {
  const offer = inSignupFlow
    ? `Choose a plan to start your ${trialLength(state?.trial_period_days)} free trial, then we'll build your forecast. ${STRIPE_LINE}`
    : `Start your free trial and pick the plan that works best for you. ${STRIPE_LINE}`;
  if (!state || state.status === "none") return offer;

  const planName = state.plan ? `${PLAN_NAMES[state.plan]} plan` : "plan";
  const periodEnd = formatBillingDate(state.current_period_end, timeZone);
  const trialEnd =
    state.status === "trialing" ? formatBillingDate(state.trial_end, timeZone) : null;

  // A cancelled trial keeps its date and says there is no charge (see describeBilling).
  if (state.cancel_at_period_end && trialEnd) {
    return `Your free trial ends on ${trialEnd}, and your ${planName} is set to cancel then, so you won't be charged. You keep full access until then.`;
  }
  if (state.is_entitled && state.cancel_at_period_end) {
    return periodEnd
      ? `Your ${planName} ends on ${periodEnd}. You keep full access until then.`
      : `Your ${planName} ends at the close of the current period. You keep full access until then.`;
  }
  if (state.status === "trialing") {
    return trialEnd && state.trial_end
      ? `You're on the ${planName}, free until ${trialEnd}. ${trialRenewalCopy(state.trial_end, state.plan, timeZone)} Manage billing to switch plans or cancel before then.`
      : `You're on the ${planName} free trial. Manage billing to switch plans or cancel.`;
  }
  if (state.status === "active") {
    const ended = trialEndedCopy(
      state.trial_end,
      state.current_period_end,
      state.plan,
      now,
      timeZone,
    );
    return periodEnd
      ? `You're on the ${planName}, which renews on ${periodEnd}.${ended ? ` ${ended}` : ""} Manage billing to update your card, switch plans or cancel.`
      : `You're on the ${planName}. Manage billing to update your card, switch plans or cancel.`;
  }
  if (
    !state.is_entitled &&
    (state.status === "canceled" || state.status === "incomplete_expired")
  ) {
    return `Your subscription has ended. Pick a plan to pick up where you left off. ${STRIPE_LINE}`;
  }
  // past_due, unpaid, incomplete, paused: the notice card below says what is wrong.
  return `Your ${planName} needs attention. Manage billing to sort it out. ${STRIPE_LINE}`;
}

/**
 * `null` for a missing or unparseable date, so callers drop the clause rather
 * than printing "Invalid Date" at the user. Stripe omits `current_period_end`
 * on a cancelled subscription, so the null path is a real case, not defensive
 * padding.
 *
 * FP-147 (Paul, 2026-10-07, "billing six"): the date where the USER is, through the
 * shared `billingDateLabel`. G2-10 (#961, #1008) pinned `timeZone: "UTC"` on the
 * premise that Stripe's instants are midnight-UTC calendar dates; that came from test
 * fixtures. A real `trial_end` / `current_period_end` is the signup instant plus the
 * period, so UTC showed a US evening sign-up's date a day late and a last-hour one
 * east of UTC a day early. Superseded.
 */
export function formatBillingDate(value: string | null, timeZone?: string): string | null {
  if (!value) return null;
  if (Number.isNaN(new Date(value).getTime())) return null;
  return billingDateLabel(value, timeZone);
}

/**
 * The trial banner's sentence, by calendar day where the user is (FP-147). It used
 * `ceil(hours left / 24)` and called anything at or under 1 "today", so a trial ending
 * at 10:00 tomorrow read "ends today" the evening before. Same thresholds as mobile's
 * `trialCountdownCopy`; web's banner speaks in full sentences.
 */
export function trialBannerCopy(
  trialEnd: string | null,
  now: Date = new Date(),
  timeZone?: string,
): string {
  if (!trialEnd || Number.isNaN(new Date(trialEnd).getTime())) return "You're on a free trial.";
  if (new Date(trialEnd).getTime() <= now.getTime()) return "Your free trial has ended.";
  const days = billingDaysUntil(trialEnd, now, timeZone);
  if (days <= 0) return "Your free trial ends today.";
  if (days === 1) return "Your free trial ends tomorrow.";
  return `${days} days left in your free trial.`;
}
